/**
 * The small shared pieces every screen is built from: avatars, pills, cards,
 * meters and empty states. Each one owns a single class from the stylesheet,
 * so a visual change happens in one place rather than in twenty templates.
 */
import Icon from '../lib/icons.jsx';
import { initials, avatarClass, lower } from '../lib/format.js';
import { formatDate, daysTo } from '../lib/dates.js';

/* --------------------------------------------------------------- avatar */

export function Avatar({ name, className = '', style, children }) {
  return (
    <div className={`who ${avatarClass(name)} ${className}`} style={style}>
      {children ?? initials(name)}
    </div>
  );
}

/* ---------------------------------------------------------------- pills */

export const Pill = ({ tone = 'p-grey', icon, className = '', children, ...rest }) => (
  <span className={`pill ${tone} ${className}`.trimEnd()} {...rest}>
    {icon ? <Icon name={icon} solid /> : null}
    {children}
  </span>
);

/*
 * Where a deal came from. Two systems feed the pipeline and they see different
 * people: the accounts API knows who signed up, the visitor tracker knows who
 * asked for a demo, and somebody who did both appears in each. Naming the
 * source on the row is what tells a 'Demo request' that has gone quiet from a
 * 'Signup' that never needed one.
 *
 * A hand-typed deal is named too rather than left blank: it is a real answer
 * to "where did this come from", and it is the one the filter calls 'manual'.
 */
const ORIGINS = {
  xyra: ['Signup', 'p-green', 'zap'],
  /* Which sync wrote the row, not what the person asked for. It said
     "Demo request" from when that was the only kind the tracker brought
     across; six kinds reach the board now, and the row itself says which. */
  vizadtraq: ['Visitor tracker', 'p-purple', 'mega'],
  manual: ['Added by hand', 'p-grey', 'edit']
};

export const OriginPill = ({ value }) => {
  const [label, tone, icon] = ORIGINS[String(value || '').toLowerCase()] || ORIGINS.manual;
  return <Pill tone={tone} icon={icon}>{label}</Pill>;
};

export const Dash = () => <span className="dim">—</span>;

export function TypePill({ value }) {
  const v = String(value || '');
  const tone = v === 'Paying' ? 'gr-green'
    : v === 'Trial' ? 'gr-blue'
      : v === 'Free' ? 'gr-violet'
        : (v === 'Internal' || v === 'Test') ? 'gr-slate' : 'gr-cyan';
  return <Pill tone={tone} className="asis">{value}</Pill>;
}

/**
 * Plan tiers, lowest to highest. Each carries its own gradient and glyph so a
 * tier is recognisable at a glance without reading the word.
 */
const PLAN_TIERS = {
  FREE:         { tone: 'gr-teal',   icon: 'tag' },
  TRIAL:        { tone: 'gr-cyan',   icon: 'clock' },
  STARTER:      { tone: 'gr-blue',   icon: 'zap' },
  GROWTH:       { tone: 'gr-green',  icon: 'chart' },
  PROFESSIONAL: { tone: 'gr-indigo', icon: 'target' },
  SUITE:        { tone: 'gr-luxe',   icon: 'star' }
};

/** The gradient a plan is drawn in, so a card can match its own badge. */
export const planTone = plan =>
  (PLAN_TIERS[String(plan || '').toUpperCase()] || { tone: plan ? 'gr-purple' : 'gr-slate' }).tone;

export function PlanPill({ value }) {
  const v = String(value || '').toUpperCase();
  if (!v) return <Dash />;
  const meta = PLAN_TIERS[v] || { tone: 'gr-purple', icon: 'tag' };
  return <Pill tone={meta.tone} icon={meta.icon}>{v}</Pill>;
}

export function PayPill({ value, paidValues }) {
  const v = String(value || '').toUpperCase();
  if (!v || v === 'N/A') return <Dash />;
  const paid = lower(paidValues).includes(v.toLowerCase());
  const tone = paid ? 'gr-green' : (v === 'OVERDUE' || v === 'FAILED') ? 'gr-red' : 'gr-amber';
  return <Pill tone={tone} icon={paid ? 'check' : 'card'}>{v}</Pill>;
}

export function StatusPill({ value }) {
  const v = String(value || '').toLowerCase();
  const tone = v === 'active' ? 'gr-green'
    : v === 'trial' ? 'gr-blue'
      : (v === 'expired' || v === 'churned') ? 'gr-red'
        : v === 'on hold' ? 'gr-amber' : 'gr-slate';
  return <Pill tone={tone} icon={v === 'active' ? 'check' : 'flag'}>{value || '—'}</Pill>;
}

export function StagePill({ value }) {
  /* Both vocabularies: the display stages the pipeline now shows (New, Sales
     talk, Demo, Proposal, Closed) and the underlying stage names other screens
     still read (Won, Lost, Qualified, Negotiation). */
  const TONES = {
    New: 'p-blue', 'Sales talk': 'p-purple', Demo: 'p-cyan', Proposal: 'p-amber', Closed: 'p-green',
    Won: 'p-green', Lost: 'p-red', Negotiation: 'p-amber', Qualified: 'p-blue'
  };
  return <Pill tone={TONES[value] || 'p-blue'}>{value}</Pill>;
}

export function LeadPill({ value }) {
  const tone = value === 'Customer' ? 'p-green'
    : value === 'Qualified' ? 'p-purple'
      : value === 'Unqualified' ? 'p-grey'
        : value === 'Contacted' ? 'p-blue' : 'p-amber';
  return <Pill tone={tone}>{value || 'New'}</Pill>;
}

/**
 * The segment a row travels under — "TVC client".
 *
 * One component because the same badge has to read identically on the
 * pipeline, on the Deals page and on the client it becomes; three copies
 * of a span would be three chances for them to drift. Nothing at all
 * when the row has no segment, which is most of them.
 */
export function SegmentPill({ value, className = '' }) {
  const v = String(value || '').trim().toUpperCase();
  if (!v) return null;
  return (
    <Pill tone="gr-violet" icon="tag" className={`segpill ${className}`.trim()} title={`Imported as ${v}`}>
      {v} client
    </Pill>
  );
}

export function OwnerPill({ name }) {
  return name
    ? <Pill tone="gr-cyan" icon="users">{name}</Pill>
    : <Pill tone="gr-red" icon="alert">Unowned</Pill>;
}

/* ----------------------------------------------------------------- date */

/** A date chip that turns amber near a deadline and red once it has passed. */
export function DatePill({ date, warnWithin, dateFmt, suffix }) {
  if (!date) return <Dash />;
  const n = daysTo(date);
  const tone = warnWithin ? (n < 0 ? ' bad' : n <= warnWithin ? ' warn' : '') : '';
  return (
    <span className={`datep${tone}`}>
      <Icon name="cal" />
      {formatDate(date, false, dateFmt)}{suffix}
    </span>
  );
}

/* ---------------------------------------------------------------- cards */

export function Card({ children, className = '', ...rest }) {
  return <div className={`card ${className}`} {...rest}>{children}</div>;
}

export function CardHead({ icon, tone = 'g-purple', title, count, badge, children }) {
  return (
    <div className="chead">
      <h3><div className={`ic ${tone}`}><Icon name={icon} solid /></div>{title}</h3>
      {count != null && count !== 0 ? <span className="pill-n">{count}</span> : null}
      {badge}
      {children ? <><div className="spacer" style={{ flex: 1 }} />{children}</> : null}
    </div>
  );
}

export const CardBody = ({ children, className = '' }) => (
  <div className={`cbody ${className}`}>{children}</div>
);

export const AIBadge = () => (
  <span className="aibadge"><Icon name="zap" solid />AI</span>
);

export const Spinner = () => <span className="spin" />;

/* --------------------------------------------------------------- states */

export function EmptyState({ icon = 'search', tone = 'g-purple', title, children }) {
  return (
    <div className="empty">
      <div className={`ic ${tone}`}><Icon name={icon} solid /></div>
      <b>{title}</b>
      {children}
    </div>
  );
}

/* --------------------------------------------------------------- meters */

/** Lifecycle bar — trial progress, or how far through a subscription term. */
export function ProgressBar({ pct, tone = '', label }) {
  if (pct === null || pct === undefined) return <Dash />;
  return (
    <div className="prog">
      <div className="track"><div className={`fill ${tone}`} style={{ width: `${pct}%` }} /></div>
      <span>{label ?? `${pct}%`}</span>
    </div>
  );
}

/**
 * How much trial is left, in words, over a bar that drains as it runs out.
 * The number is the point of this column, so it leads rather than trailing a
 * row of ticks the eye has to count.
 */
export function TrialMeter({ d, trialDays }) {
  if (d.noTrial) return <Pill tone="gr-indigo">No trial</Pill>;
  if (d.dT === null) return <Pill tone="gr-amber" icon="alert">Not set</Pill>;

  const over = d.dT < 0;
  const left = Math.max(d.dT, 0);
  const cls = over ? 'over' : d.dT <= 3 ? 'warn' : '';
  const pct = over ? 100 : Math.max(0, Math.min(100, Math.round(left / trialDays * 100)));
  const label = over ? `${Math.abs(d.dT)} ${Math.abs(d.dT) === 1 ? 'day' : 'days'} over`
    : d.dT === 0 ? 'Last day'
      : `${left} ${left === 1 ? 'day' : 'days'} left`;

  return (
    <span className={`trialm ${cls}`} title={label}>
      <b>{label}</b>
      <span className="tbar"><i style={{ width: `${pct}%` }} /></span>
    </span>
  );
}

/** AI churn-risk score, colour-banded low / medium / high. */
export function RiskBar({ value, reason, action }) {
  if (typeof value !== 'number') return <Dash />;
  const tone = value >= 67 ? 'r-hi' : value >= 34 ? 'r-md' : 'r-lo';
  return (
    <span className="riskcell" title={[reason, action && `Next: ${action}`].filter(Boolean).join('\n')}>
      <span className="riskbar">
        <span className="track">
          <span className={`fill ${tone}`} style={{ display: 'block', height: '100%', width: `${value}%` }} />
        </span>
        <b>{value}</b>
      </span>
      {/* Score only. The reason and the suggested next step are a sentence
          each and turned the column into a wall of text, so they stay on
          hover and in the client view. */}
    </span>
  );
}

/** A labelled horizontal bar, used by every breakdown panel. */
export function BarRow({ label, value, pct, tone = '', onClick }) {
  return (
    <div className={`bar${onClick ? ' clickable' : ''}`} onClick={onClick}>
      <div>
        <div className="lab"><span>{label}</span><span>{value}</span></div>
        <div className="track" style={{ marginTop: 5 }}>
          <div className={`fill ${tone}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

/*
 * Where a lead came from, as a badge rather than a word.
 *
 * The column is scanned, not read: eight rows of grey text all look the
 * same, and "Ads" next to "Referral" is the difference between a lead that
 * cost money and one that did not. One colour per source, fixed - a source
 * keeps its colour whatever else is on screen.
 */
const SOURCE_TONE = {
  ads: 'ads',
  referral: 'referral',
  'organic search': 'organic',
  organic: 'organic',
  direct: 'direct',
  'social media': 'social',
  social: 'social',
  email: 'email',
  promo: 'promo',
  partner: 'partner',
  calendly: 'partner'
};

export function SourceBadge({ name }) {
  if (!name) return <Dash />;
  const tone = SOURCE_TONE[String(name).toLowerCase()] || 'direct';
  return <span className={`lbadge src ${tone}`}>{name}</span>;
}

/** On/off switch used by the automation rules. */
export const Switch = ({ on, onClick, title }) => (
  <button type="button" className={`sw${on ? ' on' : ''}`} onClick={onClick} title={title}><i /></button>
);
